"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

// Add-ons tile — sells retail extras (primarily swimsuit/bathers hire) inside the booking
// flow, answering the "I didn't bring togs" objection before it costs the booking. Both
// variants share one catalogue endpoint (/api/addons?venue=…) and one list-row layout:
//   • <AddOnsInfoTile>     — on the pool-selection pages (desktop: in the summary rail above
//     the "Your soak" card; mobile: below the time slots). No booking exists yet at that
//     step, so "Add" stashes the selection in sessionStorage (read/writePendingAddons below)
//     rather than posting anywhere.
//   • <AddOnsCheckoutTile> — interactive, on checkout: adjusting a quantity → (debounced)
//     POST → the server recomputes the authoritative total (server-authoritative) → the order
//     summary updates. On mount, if the booking has no add-ons yet, it adopts any pending
//     selection from the info tile and persists it for real, then clears the stash — so "Add"
//     pre-booking genuinely carries through rather than being purely decorative.
// Each row is a quantity stepper (not a one-of-each toggle): the first "Add" sets the qty to
// the whole party (a group that forgot their togs all forgot), then [−]/[+] adjust it, and
// dropping to 0 removes the line. Both hide themselves entirely when no product is
// add-on-eligible for the venue.

export type Addon = {
  id: string;
  name: string;
  price: number;
  description: string | null;
  image_url: string | null;
};

// One selected add-on as a summary line: the (snapshot) name, quantity and line total.
// Surfaced to the pool-page summary (AddOnsInfoTile → BookingFlow) and the checkout
// summary (AddOnsCheckoutTile → checkout page) so each add-on shows by NAME rather than
// a single lumped "Add-ons" row.
export type AddonLine = { name: string; qty: number; total: number };

const nzd = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n);

// Server's per-line sanity cap (set_booking_addons silently clamps anything over 20). The
// UI must mirror it, or the displayed total would disagree with what the server stores.
const MAX_QTY = 20;

// The qty a first "Add" defaults to: the whole party, clamped to 1..MAX_QTY. Falls back to 1
// so the stepper is never broken just because a parent forgot to pass the party size.
function firstQty(partySize?: number): number {
  const p = partySize && partySize > 0 ? Math.floor(partySize) : 1;
  return Math.min(MAX_QTY, Math.max(1, p));
}

function useAddonCatalogue(venue: string): Addon[] | null {
  const [addons, setAddons] = useState<Addon[] | null>(null);
  useEffect(() => {
    let active = true;
    // no-store: the catalogue is staff-toggled in real time, so never reuse a cached
    // (possibly empty) response — that would keep the tile hidden after a change.
    fetch(`/api/addons?venue=${encodeURIComponent(venue)}`, { cache: "no-store" })
      .then((r) => r.json())
      .then((j) => {
        if (active) setAddons((j.addons ?? []) as Addon[]);
      })
      .catch(() => {
        if (active) setAddons([]);
      });
    return () => {
      active = false;
    };
  }, [venue]);
  return addons;
}

/* ───────────────────── pre-booking selection stash (sessionStorage) ───────────────────── */
// No booking exists on the pool-selection pages, so a pre-checkout "Add" has nowhere real to
// post to yet. Stash it here; AddOnsCheckoutTile adopts + clears it the moment a real booking
// exists (and only if the booking has no add-ons of its own already — never overwrites a
// resumed booking's saved selection). The value is the qty, so quantities > 1 round-trip.

const PENDING_ADDONS_KEY = "aluume_pending_addons";

function readPendingAddons(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(PENDING_ADDONS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}
function writePendingAddons(next: Record<string, number>) {
  try {
    sessionStorage.setItem(PENDING_ADDONS_KEY, JSON.stringify(next));
  } catch {
    /* sessionStorage unavailable (private mode) — selection just won't carry to checkout */
  }
}
function clearPendingAddons() {
  try {
    sessionStorage.removeItem(PENDING_ADDONS_KEY);
  } catch {
    /* nothing to clear */
  }
}

/* ─────────────────────────────── shared list row ─────────────────────────────── */
// One product line: image + name + description + price, with a quantity control on the right.
// qty 0 → the "Add" pill; qty ≥ 1 → a [−] n [+] stepper. Tight vertical rhythm (name/desc/
// price stacked at gap-0.5, no padding around the price) so both placements stay compact per
// the checkout/pool-page spacing feedback.

function AddonRow({
  addon,
  qty,
  partySize,
  tight,
  onQty,
}: {
  addon: Addon;
  // Current quantity for this product (0 = not in cart).
  qty: number;
  // Guests on the booking — the qty a first "Add" defaults to (see firstQty).
  partySize?: number;
  // `tight` drops the gap directly UNDER the product name (name → price sit flush) — used
  // on the pool-selection tile per the spacing feedback; checkout keeps the small gap.
  tight?: boolean;
  // Report the desired next quantity. 0 removes the line; the tile clamps to 0..MAX_QTY.
  onQty: (next: number) => void;
}) {
  const inCart = qty > 0;
  return (
    <li className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="flex min-w-0 items-center gap-3">
        {addon.image_url ? (
          <Image
            src={addon.image_url}
            alt=""
            width={72}
            height={72}
            className="size-16 shrink-0 rounded-xl border border-ink/10 object-cover"
          />
        ) : (
          <span className="size-16 shrink-0 rounded-xl bg-ink/[0.06]" aria-hidden />
        )}
        {/* Two base-layer rules in tokens.css both inflate the name→price gap, and BOTH must be
            neutralised on every line here (this gap has been reported repeatedly):
              1. `body { line-height: 1.55 }` (--lh-body) is inherited by every unstyled <p>; at
                 13–14.5px that adds ~4px of leading above AND below each line. → pin each line to
                 the design-system tight token (--lh-tight, 1.25).
              2. `p { margin: 0 0 1em }` (tokens.css base layer, NOT Tailwind preflight) gives every
                 <p> a bottom margin equal to its own font-size (~13–14.5px) — the dominant cause,
                 3–4× the leading difference. → `m-0` on all three lines.
            Applies to BOTH the pool-selection (tight) and checkout paths — one shared row. */}
        <div className={`flex min-w-0 flex-col leading-[var(--lh-tight)] ${tight ? "gap-0" : "gap-0.5"}`}>
          <p className="m-0 text-[14.5px] leading-[var(--lh-tight)] text-ink">{addon.name}</p>
          {addon.description && (
            <p className="m-0 line-clamp-1 text-[12.5px] font-light leading-[var(--lh-tight)] text-ink-soft">
              {addon.description}
            </p>
          )}
          <p className="m-0 text-[13px] font-light leading-[var(--lh-tight)] text-ink-soft">
            {nzd(Number(addon.price))}
          </p>
        </div>
      </div>
      {/* Reserve the control's footprint so the row never reflows when it flips between the
          "Add" pill and the stepper (both have roughly the same width). */}
      <div className="flex min-w-[92px] shrink-0 justify-end">
        {inCart ? (
          <div className="inline-flex items-center rounded-pill border border-ink/[0.2]">
            <button
              type="button"
              onClick={() => onQty(qty - 1)}
              aria-label={`Remove one ${addon.name}`}
              className="flex size-8 items-center justify-center rounded-l-pill text-[16px] font-light text-ink-soft transition-colors hover:text-accent"
            >
              −
            </button>
            <span
              aria-live="polite"
              className="min-w-[24px] text-center text-[13px] tabular-nums text-ink"
            >
              {qty}
            </span>
            <button
              type="button"
              onClick={() => onQty(Math.min(MAX_QTY, qty + 1))}
              disabled={qty >= MAX_QTY}
              aria-label={`Add another ${addon.name}`}
              className="flex size-8 items-center justify-center rounded-r-pill text-[16px] font-light text-ink transition-colors hover:text-accent disabled:opacity-30"
            >
              +
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onQty(firstQty(partySize))}
            className="rounded-pill border border-ink/[0.2] px-4 py-2 text-[12px] uppercase tracking-[0.4px] text-ink transition-colors hover:border-accent hover:text-accent"
          >
            Add
          </button>
        )}
      </div>
    </li>
  );
}

/* ───────────────────────── informational (pool pages) ───────────────────────── */

export function AddOnsInfoTile({
  venue,
  className,
  partySize,
  onSelectionChange,
}: {
  venue: "bathe" | "thermae";
  className?: string;
  /** Guests on the booking-to-be — the qty a first "Add" defaults to. */
  partySize?: number;
  /** Reports the resolved add-on lines so the pool-page summary can show them + fold
   *  their value into the running total. Fires on load and on every change. */
  onSelectionChange?: (lines: AddonLine[]) => void;
}) {
  const addons = useAddonCatalogue(venue);
  const [sel, setSel] = useState<Record<string, number>>({});

  // Reflect a selection already staged on an earlier visit / the other breakpoint's copy.
  useEffect(() => {
    setSel(readPendingAddons());
  }, []);

  // Surface the current selection (resolved to name + price via the catalogue) to the
  // parent summary. onSelectionChange is a stable callback (BookingFlow memoises it), so
  // this only re-runs when the catalogue loads or the selection changes.
  useEffect(() => {
    if (!onSelectionChange) return;
    const lines: AddonLine[] = (addons ?? [])
      .filter((a) => (sel[a.id] ?? 0) > 0)
      .map((a) => ({
        name: a.name,
        qty: sel[a.id],
        total: Math.round(Number(a.price) * sel[a.id] * 100) / 100,
      }));
    onSelectionChange(lines);
  }, [addons, sel, onSelectionChange]);

  if (!addons || addons.length === 0) return null; // nothing eligible → render nothing
  const anySelected = Object.keys(sel).length > 0;

  // Set the qty at CLICK TIME only — never retro-adjust when the party changes afterwards.
  // 0 removes the line (back to the "Add" pill); everything is clamped to 0..MAX_QTY.
  function setQty(id: string, next: number) {
    const n = Math.min(MAX_QTY, Math.max(0, Math.floor(next)));
    const nextSel = { ...sel };
    if (n <= 0) delete nextSel[id];
    else nextSel[id] = n;
    setSel(nextSel);
    writePendingAddons(nextSel);
  }

  return (
    <section className={className}>
      <div className="rounded-2xl border border-ink/10 bg-white p-6">
        <p className="eyebrow text-ink-mute">Before you arrive</p>
        <h3
          style={{ fontFamily: "var(--font-display)" }}
          className="mt-1.5 text-[22px] font-light text-ink"
        >
          Forgot your togs?
        </h3>
        <ul className="mt-4 flex flex-col divide-y divide-ink/10">
          {addons.map((a) => (
            <AddonRow
              key={a.id}
              addon={a}
              qty={sel[a.id] ?? 0}
              partySize={partySize}
              tight
              onQty={(n) => setQty(a.id, n)}
            />
          ))}
        </ul>
        {anySelected && (
          <p className="mt-4 text-[12.5px] font-light text-ink-soft">
            Added — you&apos;ll see it in your total and again at checkout to adjust or remove.
          </p>
        )}
      </div>
    </section>
  );
}

/* ───────────────────────── interactive (checkout) ───────────────────────── */

type ServerAddonItem = {
  product_id: string | null;
  name: string;
  unit_price: number;
  qty: number;
};

// Server add-on rows → summary lines (name + qty + snapshot line total).
function linesFromItems(items: ServerAddonItem[]): AddonLine[] {
  return items.map((it) => ({
    name: it.name,
    qty: Number(it.qty),
    total: Math.round(Number(it.unit_price) * Number(it.qty) * 100) / 100,
  }));
}

export function AddOnsCheckoutTile({
  bookingId,
  venue,
  partySize,
  onTotals,
}: {
  bookingId: string;
  venue: string;
  /** Guests on the booking — the qty a first "Add" defaults to. */
  partySize?: number;
  /** Called after each server recompute with the add-on total and the per-item lines
   *  (so the order summary can list each add-on by name). */
  onTotals: (t: { addonsTotal: number; lines: AddonLine[] }) => void;
}) {
  const addons = useAddonCatalogue(venue);
  const [sel, setSel] = useState<Record<string, number>>({});

  // Debounced, optimistic commit. The stepper updates `sel` immediately and stays ENABLED;
  // the POST is coalesced ~400ms after the last tap so rapid +/− costs a single round-trip.
  // Out-of-order replies are dropped by sequence number, so a slow earlier response can't
  // overwrite a newer selection's server total. The RPC replaces the WHOLE set each call, so
  // we always POST the full selection, never a delta.
  const seqRef = useRef(0); // id of the most recent request issued
  const appliedRef = useRef(0); // id of the most recent reply applied to the totals
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commit = useCallback(
    async (next: Record<string, number>, seq: number) => {
      try {
        const items = Object.entries(next)
          .filter(([, n]) => n > 0)
          .map(([product_id, n]) => ({ product_id, qty: n }));
        const res = await fetch(`/api/bookings/${bookingId}/addons`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });
        const j = await res.json();
        if (!res.ok) return;
        if (seq < appliedRef.current) return; // a newer reply already won — drop this one
        appliedRef.current = seq;
        onTotals({
          addonsTotal: Number(j.addons_total ?? 0),
          lines: linesFromItems((j.items ?? []) as ServerAddonItem[]),
        });
      } catch {
        /* keep the last-known totals; the server recomputes authoritatively at payment */
      }
    },
    [bookingId, onTotals],
  );

  // Keep the freshest commit in a ref so the debounce timer and the mount effect can call it
  // without depending on commit's identity (which changes each parent render via onTotals) —
  // that dependency would otherwise reset the timer / re-fetch on every render.
  const commitRef = useRef(commit);
  useEffect(() => {
    commitRef.current = commit;
  }, [commit]);

  // Coalesce POSTs: reset the timer on each change and fire once, ~400ms after the last tap.
  const scheduleCommit = useCallback((next: Record<string, number>) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const seq = ++seqRef.current;
      void commitRef.current(next, seq);
    }, 400);
  }, []);

  // Flush any pending debounce on unmount so we don't POST after the tile is gone.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  // Pre-fill from any add-ons already saved on this hold (resume / back-nav), and surface
  // their total + lines to the summary so a resumed booking shows them immediately. If the
  // booking has NOTHING saved yet, adopt any selection stashed by AddOnsInfoTile pre-booking,
  // persist it for real, and clear the stash — so "Add" on the pool-selection page carries
  // through once (at whatever qty was chosen). Keyed on bookingId only → runs once per hold.
  useEffect(() => {
    let active = true;
    fetch(`/api/bookings/${bookingId}/addons`)
      .then((r) => r.json())
      .then((j) => {
        if (!active) return;
        const items = (j.items ?? []) as ServerAddonItem[];
        const q: Record<string, number> = {};
        let cents = 0;
        for (const it of items) {
          if (it.product_id) q[it.product_id] = Number(it.qty);
          cents += Math.round(Number(it.unit_price) * 100) * Number(it.qty);
        }
        if (Object.keys(q).length === 0) {
          const pending = readPendingAddons();
          if (Object.keys(pending).length > 0) {
            clearPendingAddons();
            setSel(pending);
            const seq = ++seqRef.current;
            void commitRef.current(pending, seq); // persist immediately (no debounce on adopt)
            return;
          }
        }
        setSel(q);
        onTotals({ addonsTotal: cents / 100, lines: linesFromItems(items) });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // onTotals/commit intentionally omitted — captured once; we only want this per booking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // Set the qty at CLICK TIME only — never retro-adjust when the party changes. Update local
  // state optimistically (the stepper stays responsive) and debounce the server commit; the
  // server response stays authoritative for the totals. 0 removes the line.
  const setQty = (id: string, next: number) => {
    const n = Math.min(MAX_QTY, Math.max(0, Math.floor(next)));
    const nextSel = { ...sel };
    if (n <= 0) delete nextSel[id];
    else nextSel[id] = n;
    setSel(nextSel);
    scheduleCommit(nextSel);
  };

  if (!addons || addons.length === 0) return null; // nothing eligible → render nothing

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-ink/[0.14] bg-white p-5 md:gap-4 md:p-6">
      <div>
        <p className="eyebrow text-ink-mute">Add-ons</p>
        <p className="mt-1.5 text-[14px] font-light text-ink-soft">
          Forgot your togs? Add swimsuit hire and more — it&apos;s added to your booking total.
        </p>
      </div>
      <ul className="flex flex-col divide-y divide-ink/10">
        {addons.map((a) => (
          <AddonRow
            key={a.id}
            addon={a}
            qty={sel[a.id] ?? 0}
            partySize={partySize}
            onQty={(n) => setQty(a.id, n)}
          />
        ))}
      </ul>
    </div>
  );
}
